/**
 * What the open book can lose today — ROADMAP 6.12.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * ONE BAND PER UNDERLYING, NEVER ONE FOR THE BOOK
 *
 * "Spot moves 500 points" is a sentence about ONE index. A book holding NIFTY
 * and SENSEX has two spots, two move sizes and no shared axis, so the legs are
 * grouped by underlying, each group is evaluated against its own band, and the
 * results are added.
 *
 * Adding is right ACROSS underlyings and wrong WITHIN one. Two NIFTY baskets
 * lose at different spots, so summing their worst cases describes a day that
 * cannot happen — `portfolioRisk` pools their legs instead and lets a long in
 * one offset a short in the other. Two different indices genuinely can have
 * their bad day at once, so there the sum is the honest answer.
 *
 * THE MOVE SIZE IS A PERCENTAGE, AND THE UI SAYS SO
 *
 * `DEFAULT_MOVE_PCT` is a flat 2% of spot, not an implied-volatility move.
 * That is a deliberate first version: IV is on the chain, the chain is a
 * different screen, and a number derived from a quote the drawer cannot see
 * would be harder to explain than one the label states outright. The band is
 * an input precisely so this can become a 1-day sigma later without anything
 * downstream changing.
 * ─────────────────────────────────────────────────────────────────────────────
 */

import { useMemo } from 'react'
import { maxRisk, portfolioMaxRisk, type OpenRisk, type PayoffLeg } from '@apna/shared'
import { heldPayoff } from './heldPayoff'
import type { OpenBookTrade } from './useOpenBook'

/** The risk of one trade, and of the whole book. */
export interface BookRisk {
  /** Keyed by `tradeId`. Absent for a trade that holds nothing. */
  readonly byTrade: ReadonlyMap<string, OpenRisk>
  /** Every underlying's risk, added. */
  readonly total: OpenRisk
}

const FLAT: OpenRisk = { kind: 'FLAT' }

export function useOpenRisk(trades: readonly OpenBookTrade[]): BookRisk {
  /*
   * ── NO TICKS, AND THAT IS THE FEATURE — ROADMAP 6.13 ────────────────────
   *
   * This hook used to subscribe to every underlying's spot and every leg's
   * mark, solve an implied volatility from each, build a one-session band
   * out of them and price the basket forward with Black-76. Every one of
   * those inputs moves continuously, so the number moved continuously: the
   * drawer read "-1.94 L if ±2,194 pts" and both halves changed on every
   * frame.
   *
   * Reported 2026-11-01 as the wrong answer to the right question. What this
   * line is FOR is "how much is at stake here", and that must not move
   * unless the position does — a figure that cannot be remembered between
   * two glances cannot be the one a size decision is made from.
   *
   * `maxRisk` takes LEGS and nothing else: strikes, signed quantities and
   * the prices already paid, all frozen. No spot, no volatility, no clock.
   * So the memo below depends on `trades` alone, and the whole tick
   * subscription this hook used to carry is gone with it.
   */
  return useMemo(() => {
    const byTrade = new Map<string, OpenRisk>()
    /** Underlying -> every priceable leg held in it. */
    const pool = new Map<string, PayoffLeg[]>()

    for (const trade of trades) {
      const priced = heldPayoff(trade)
      if (priced.kind !== 'READY') {
        /*
         * `heldPayoff` already refuses a trade with incomplete contract data
         * or a mixed expiry, and its sentence is the honest one. Carried
         * through rather than re-worded, and NEVER turned into a zero.
         */
        byTrade.set(trade.tradeId, { kind: 'UNKNOWN', why: priced.why })
        continue
      }

      /*
       * A calendar read at its nearest expiry gave up the later legs' time
       * value to get here, so the figure is an upper bound on the loss.
       * Carried onto the result rather than recomputed — `maxRisk` is exact
       * arithmetic and has no way to know its inputs were pessimised.
       */
      const risk = maxRisk(priced.legs)
      byTrade.set(
        trade.tradeId,
        priced.conservative && risk.kind === 'RISK' ? { ...risk, conservative: true } : risk,
      )

      const held = pool.get(trade.underlying)
      if (held) held.push(...priced.legs)
      else pool.set(trade.underlying, [...priced.legs])
    }

    /*
     * An UNKNOWN anywhere makes the TOTAL unknown. A portfolio figure that
     * silently left out the basket it could not price would be the most
     * dangerous number on the screen — confidently smaller than the truth,
     * and smallest exactly when something is wrong.
     */
    const unpriceable = [...byTrade.values()].find((r) => r.kind === 'UNKNOWN')
    if (unpriceable !== undefined && unpriceable.kind === 'UNKNOWN') {
      return { byTrade, total: { kind: 'UNKNOWN', why: unpriceable.why } }
    }

    /*
     * Pooled WITHIN an underlying and added across them. Pooling lets a long
     * call in one basket offset a short call in another; two underlyings
     * share no spot axis, so their worst cases are added instead.
     */
    let total: OpenRisk = FLAT
    for (const legs of pool.values()) total = add(total, portfolioMaxRisk([legs]))

    /*
     * ONE BOUNDED BASKET MAKES THE WHOLE TOTAL A BOUND. The pool holds the
     * pessimised legs, so the sum is pessimised too — and a total that
     * presented itself as exact while one of its parts was not would be the
     * same understatement this file refuses everywhere else, dressed as
     * precision.
     */
    const bounded = [...byTrade.values()].some((r) => r.kind === 'RISK' && r.conservative === true)
    if (bounded && total.kind === 'RISK') total = { ...total, conservative: true }

    return { byTrade, total }
  }, [trades])
}

/**
 * Add one underlying's risk to the running total.
 *
 * UNKNOWN is absorbing: a total that dropped the part it could not price
 * would understate the book, and it would do so at the moment something is
 * already wrong.
 */
function add(a: OpenRisk, b: OpenRisk): OpenRisk {
  if (a.kind === 'UNKNOWN') return a
  if (b.kind === 'UNKNOWN') return b
  if (a.kind === 'FLAT') return b
  if (b.kind === 'FLAT') return a

  /*
   * The two tails belong to different indices and cannot be combined into one
   * rate — "rupees per point" has no meaning once the points are NIFTY's and
   * SENSEX's at once. The faster one is reported, because one line under
   * pressure should carry the number that hurts most.
   */
  const beyond =
    a.beyond && b.beyond ? (a.beyond.perPoint >= b.beyond.perPoint ? a.beyond : b.beyond) : (a.beyond ?? b.beyond)

  return {
    kind: 'RISK',
    worst: a.worst + b.worst,
    // The spot of the WORSE half; a combined figure has no single spot.
    atSpot: a.worst <= b.worst ? a.atSpot : b.atSpot,
    best: a.best + b.best,
    beyond,
  }
}
